import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Expand, ImageOff, X, ZoomIn, ZoomOut } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { auctionHeroSrcSet, transformSupabaseImage } from "@/lib/image-transform";
import { CardAssetImage, isCardAssetUrl } from "@/components/card-asset-image";

const HERO_SIZES = "(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 640px";

/**
 * Premium media gallery for the auction detail page.
 * Data comes from `images` prop — no data fetching or side effects.
 */
export function MediaGallery({
  images,
  title,
}: {
  images: string[] | null | undefined;
  title: string;
}) {
  const imgs = (images ?? []).filter(Boolean);
  const altFor = (idx: number) =>
    imgs.length > 1 ? `${title} — photo ${idx + 1} of ${imgs.length}` : title;
  const hasMany = imgs.length > 1;
  const [i, setI] = useState(0);
  // Guard against the image list shrinking underneath the selected index.
  const idx = Math.min(i, Math.max(0, imgs.length - 1));
  const current = imgs[idx] ?? "";
  const [fs, setFs] = useState(false);
  const fullscreenSrc = isCardAssetUrl(current)
    ? current.replace(/([?&])v=[^&]*/, "$1v=ultra")
    : current;
  const [zoom, setZoom] = useState(1);
  const [touchX, setTouchX] = useState<number | null>(null);

  const go = useCallback(
    (delta: number) => {
      if (imgs.length === 0) return;
      setI((c) => (c + delta + imgs.length) % imgs.length);
      setZoom(1);
    },
    [imgs.length],
  );

  useEffect(() => {
    if (!fs) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "Escape") setFs(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fs, go]);

  const openFullscreen = () => {
    setFs(true);
    setZoom(1);
    track("gallery_open", { image_count: imgs.length });
  };

  const onTouchStart = (e: React.TouchEvent) => setTouchX(e.touches[0]?.clientX ?? null);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    setTouchX(null);
  };

  if (imgs.length === 0) {
    return (
      <div className="aspect-[3/4] rounded-2xl border border-border/60 bg-surface-2 overflow-hidden gold-ring flex items-center justify-center text-muted-foreground">
        <ImageOff className="h-10 w-10" />
      </div>
    );
  }

  return (
    <div>
      <div
        className="group relative aspect-[3/4] cursor-zoom-in rounded-2xl border border-border/60 bg-surface-2 overflow-hidden gold-ring"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {isCardAssetUrl(current) ? (
          // Pipeline-backed photo: one stable URL that upgrades itself to the
          // studio render as soon as processing finishes.
          <CardAssetImage
            key={current}
            src={current}
            alt={altFor(idx)}
            width={900}
            height={1200}
            className="w-full h-full object-contain bg-black/20 animate-fade-in transition-transform duration-500 ease-out group-hover:scale-[1.02]"
            fetchPriority={idx === 0 ? "high" : undefined}
            loading={idx === 0 ? "eager" : "lazy"}
            draggable={false}
            onClick={openFullscreen}
          />
        ) : (
          <picture key={current}>
            <source
              type="image/avif"
              srcSet={auctionHeroSrcSet(current, "avif")}
              sizes={HERO_SIZES}
            />
            <source
              type="image/webp"
              srcSet={auctionHeroSrcSet(current, "webp")}
              sizes={HERO_SIZES}
            />
            <img
              src={transformSupabaseImage(current, { width: 720, quality: 72 })}
              srcSet={auctionHeroSrcSet(current)}
              sizes={HERO_SIZES}
              alt={altFor(idx)}
              width={900}
              height={1200}
              className="w-full h-full object-contain bg-black/20 animate-fade-in transition-transform duration-500 ease-out group-hover:scale-[1.02]"
              fetchPriority={idx === 0 ? "high" : undefined}
              loading={idx === 0 ? "eager" : "lazy"}
              decoding={idx === 0 ? "sync" : "async"}
              draggable={false}
              onClick={openFullscreen}
            />
          </picture>
        )}

        <button
          type="button"
          onClick={openFullscreen}
          aria-label="Open image full screen"
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-3 py-1.5 text-xs text-white/90 transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
        >
          <Expand className="h-3.5 w-3.5" /> View
        </button>

        {hasMany && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 backdrop-blur-md text-white/90 transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 backdrop-blur-md text-white/90 transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 backdrop-blur-md px-3 py-1 text-[11px] uppercase tracking-widest text-white/80">
              {idx + 1} / {imgs.length}
            </div>
          </>
        )}
      </div>

      {hasMany && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-5 sm:overflow-visible">
          {imgs.map((img, thumbIdx) => (
            <button
              key={thumbIdx}
              type="button"
              onClick={() => {
                setI(thumbIdx);
                setZoom(1);
              }}
              aria-label={`Show image ${thumbIdx + 1}`}
              aria-pressed={thumbIdx === idx}
              className={cn(
                "aspect-square w-16 shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-300 ease-out press-tap sm:w-auto",
                thumbIdx === idx
                  ? "border-gold shadow-[0_0_0_2px_rgba(212,175,55,0.15)]"
                  : "border-border/60 opacity-70 hover:opacity-100 hover:border-primary/50",
              )}
            >
              {isCardAssetUrl(img) ? (
                <CardAssetImage
                  src={img}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <img
                  src={transformSupabaseImage(img, { width: 200, quality: 70, resize: "cover" })}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              )}
            </button>
          ))}
        </div>
      )}


      <Dialog open={fs} onOpenChange={setFs}>
        <DialogContent
          className="max-w-[96vw] w-[96vw] sm:max-w-6xl p-0 bg-black/95 border-white/10 overflow-hidden [&>button.absolute]:hidden"
        >
          <DialogTitle className="sr-only">{title} — image {idx + 1} of {imgs.length}</DialogTitle>
          <div className="relative h-[90vh] flex items-center justify-center">
            {isCardAssetUrl(current) ? (
              // Full screen deserves the highest rendition the pipeline has.
              <CardAssetImage
                src={fullscreenSrc}
                alt={altFor(idx)}
                className="max-h-full max-w-full object-contain transition-transform duration-200"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            ) : (
              <img
                src={current}
                alt={altFor(idx)}
                className="max-h-full max-w-full object-contain transition-transform duration-200"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            )}

            <div className="absolute top-3 right-3 flex gap-2">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
                aria-label="Zoom out"
                disabled={zoom <= 1}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white disabled:opacity-40"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
                aria-label="Zoom in"
                disabled={zoom >= 3}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white disabled:opacity-40"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setFs(false)}
                aria-label="Close full screen"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {hasMany && (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-widest text-white/80">
                  {idx + 1} / {imgs.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
